import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiService } from '@/lib/api'
import useWebSocket from '@/hooks/useWebSocket'
import DashboardLayout from '@/components/DashboardLayout'
import FileViewerModal from '@/components/FileViewerModal'
import { User } from '@/lib/api'
import {
  FileText,
  Building2,
  Calendar,
  ChevronDown,
  ChevronUp,
  BrainCircuit,
  CheckCircle2,
  Clock,
  AlertCircle,
  LayoutGrid,
  List,
  Filter,
  Wifi,
  WifiOff,
  Send,
  Loader2,
  Download,
  Sparkles,
  Info,
  Edit2,
  X,
  Check,
  ScanLine,
  FileDigit,
  Activity,
  Zap,
  Circle,
  Eye
} from 'lucide-react'

interface Attachment {
  id: number
  filename: string
  content_type: string
  byte_size: number
  url: string
}

interface UserData {
  id: number
  email: string
  full_name: string
}

interface Message {
  id: number
  content: string
  role: 'user' | 'assistant' | 'system'
  attachments_count: number
  created_at: string
  user?: UserData
  attachments?: Attachment[]
}

interface UserSelection {
  account_code: string
  account_name: string
  change_reason: string
  changed_at?: string
  changed_by_id?: number
  changed_by_name?: string
}

interface EffectiveAccount {
  account_code: string
  account_name: string
}

interface ExtractedLineItem {
  description: string
  quantity?: number
  unit_price?: number
  amount: number
  is_tax?: boolean
  suggested_account?: {
    account_code: string
    account_name: string
    confidence: number
    reasoning: string
  }
  effective_account?: EffectiveAccount
  is_user_modified?: boolean
  user_selection?: UserSelection | null
}

interface ExtractedData {
  vendor_or_customer: string
  document_number: string
  document_date: string
  due_date?: string
  total_amount: number
  subtotal?: number
  tax_amount?: number
  currency: string
  line_items: ExtractedLineItem[]
}

interface FileUpload {
  id: number
  status: string
  files_count: number
  requires_review: boolean
  ai_suggestion_ready: boolean
  can_create_bill: boolean
  confidence: number
  filename?: string
  original_filename?: string
  file_size?: number
  attachment_id?: number
  extracted_data?: ExtractedData
  suggested_account?: {
    id: number
    account_code: string
    account_name: string
  }
  created_bill?: {
    id: number
    bill_number: string
    total_amount: number
    status: string
    has_transaction: boolean
  }
}

interface ChatSession {
  id: number
  title: string
  status: string
  context?: string
  messages_count: number
  user: UserData
  created_at: string
  updated_at: string
  messages?: Message[]
  last_message?: Message
  file_uploads?: FileUpload[]
}

interface SuggestedAccount {
  account_code: string
  account_name: string
  confidence: number
  reasoning: string
}

interface LineItem {
  id: number
  index: number // line_item_index for API calls
  desc: string
  quantity?: number
  unit_price?: number
  amount: number
  isTax?: boolean
  suggestedAccount?: SuggestedAccount
  effectiveAccount?: EffectiveAccount
  isUserModified?: boolean
  userSelection?: UserSelection | null
}

interface ProcessedDocument {
  id: string
  fileName: string
  fileSize: string
  vendor: string
  transactionType: string
  docType: string
  docNum: string
  date: string
  amount: number
  currency: string
  confidence: number
  suggestedAccount: string
  status: 'pending_review' | 'verified'
  userFeedback: string
  lineItems: LineItem[]
  fileUploadId?: number
}

// Confidence Badge Component
const ConfidenceBadge = ({ score }: { score: number }) => {
  let colorClass = 'bg-rose-500/10 text-rose-400 border-rose-500/20'
  if (score >= 0.9) colorClass = 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shadow-[0_0_10px_rgba(16,185,129,0.1)]'
  else if (score >= 0.7) colorClass = 'bg-amber-500/10 text-amber-400 border-amber-500/20'

  return (
    <div className={`px-2.5 py-0.5 rounded-full text-xs font-medium border inline-flex items-center gap-1 ${colorClass}`}>
      {Math.round(score * 100)}% Match
    </div>
  )
}

// Processing Loader Component - shown while AI is analyzing documents
const ProcessingLoader = () => {
  const [processingStep, setProcessingStep] = useState(0)
  const [isComplete, setIsComplete] = useState(false)

  const steps = [
    { text: "OCR Scanning text layers", icon: ScanLine, detail: "Detecting characters & layout..." },
    { text: "Extracting data points", icon: FileDigit, detail: "Identifying dates, amounts, taxes..." },
    { text: "Classifying vendor", icon: Activity, detail: "Matching vendor to expense category..." },
    { text: "Smart Classification", icon: CheckCircle2, detail: "Matching to Chart of Accounts..." }
  ]

  useEffect(() => {
    if (isComplete) return

    const interval = setInterval(() => {
      setProcessingStep((prev) => {
        if (prev >= steps.length - 1) {
          setIsComplete(true)
          return steps.length
        }
        return prev + 1
      })
    }, 2000)
    return () => clearInterval(interval)
  }, [isComplete, steps.length])

  const progressPercent = isComplete ? 100 : Math.min(((processingStep) / steps.length) * 100, 100)

  return (
    <div className="relative">
      {/* Background ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-cyan-500/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 flex flex-col md:flex-row items-center gap-12 justify-center min-h-[400px] relative overflow-hidden group">

        {/* Corner Accents */}
        <div className="absolute top-0 left-0 w-32 h-32 border-l border-t border-cyan-500/20 rounded-tl-2xl"></div>
        <div className="absolute bottom-0 right-0 w-32 h-32 border-r border-b border-cyan-500/20 rounded-br-2xl"></div>

        {/* LEFT SIDE: Central Loader Animation */}
        <div className="relative w-48 h-64 flex-shrink-0" style={{ perspective: '1000px' }}>
          {/* Document Card */}
          <div className="absolute inset-0 bg-slate-800 rounded-lg border border-slate-700 shadow-2xl flex flex-col p-4 overflow-hidden transition-transform duration-500 hover:rotate-0"
               style={{ transform: 'rotateY(6deg) rotateX(6deg)' }}>
             {/* Mock Document Content */}
             <div className="w-full h-4 bg-slate-700 rounded mb-4 animate-pulse"></div>
             <div className="space-y-2">
               <div className="w-3/4 h-2 bg-slate-700/50 rounded"></div>
               <div className="w-full h-2 bg-slate-700/50 rounded"></div>
               <div className="w-5/6 h-2 bg-slate-700/50 rounded"></div>
               <div className="w-full h-2 bg-slate-700/50 rounded"></div>
             </div>
             <div className="mt-6 flex justify-between">
               <div className="w-12 h-12 bg-slate-700/30 rounded-full"></div>
               <div className="space-y-2 flex-1 ml-4">
                 <div className="w-full h-2 bg-slate-700/50 rounded"></div>
                 <div className="w-2/3 h-2 bg-slate-700/50 rounded"></div>
               </div>
             </div>

             {/* Scanning Beam */}
             <div className="absolute inset-x-0 h-1 bg-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.8)] z-10 animate-scan"></div>

             {/* Highlighted 'Read' Area (follows beam) */}
             <div className="absolute inset-x-0 top-0 bg-cyan-500/10 animate-scanOverlay origin-top"></div>
          </div>

          {/* Floating Particles/Nodes */}
          <div className="absolute -right-6 top-10 w-3 h-3 bg-cyan-500 rounded-full animate-ping opacity-75"></div>
          <div className="absolute -left-4 bottom-20 w-2 h-2 bg-blue-500 rounded-full animate-ping opacity-50" style={{ animationDelay: '700ms' }}></div>
        </div>

        {/* RIGHT SIDE: Detailed Process Timeline */}
        <div className="z-10 w-full max-w-md">

          <div className="flex items-center gap-3 mb-6">
             <div className={`p-2 rounded-lg ${isComplete ? 'bg-emerald-500/10' : 'bg-cyan-500/10'}`}>
                {isComplete ? (
                  <Sparkles className="w-5 h-5 text-emerald-400 animate-pulse" />
                ) : (
                  <Zap className="w-5 h-5 text-cyan-400 animate-pulse" />
                )}
             </div>
             <div>
                <h4 className="text-white font-semibold">
                  {isComplete ? 'Finalizing Results' : 'AI Analysis in Progress'}
                </h4>
                <p className="text-xs text-slate-500">
                  {isComplete ? 'Almost there, preparing your document review...' : 'Processing your document...'}
                </p>
             </div>
             <div className={`ml-auto font-mono text-sm font-bold ${isComplete ? 'text-emerald-400' : 'text-cyan-400'}`}>
                {Math.round(progressPercent)}%
             </div>
          </div>

          {/* The Steps List */}
          <div className="space-y-4 relative">
             {/* Vertical Line */}
             <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-slate-800 -z-10"></div>

             {steps.map((step, idx) => {
               const isCompleted = idx < processingStep
               const isCurrent = idx === processingStep
               const isPending = idx > processingStep
               const StepIcon = step.icon

               return (
                 <div key={idx} className={`flex items-start gap-4 p-2 rounded-lg transition-all duration-300 ${isCurrent ? 'bg-slate-800/40 border border-slate-700/50' : ''}`}>
                    {/* Step Icon Indicator */}
                    <div className={`
                       w-8 h-8 rounded-full flex items-center justify-center border-2 flex-shrink-0 z-10 bg-slate-900
                       ${isCompleted ? 'border-emerald-500 bg-emerald-500/10 text-emerald-500' : ''}
                       ${isCurrent ? 'border-cyan-500 text-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.3)]' : ''}
                       ${isPending ? 'border-slate-700 text-slate-600' : ''}
                    `}>
                       {isCompleted ? (
                         <CheckCircle2 className="w-4 h-4" />
                       ) : isCurrent ? (
                         <Loader2 className="w-4 h-4 animate-spin" />
                       ) : (
                         <Circle className="w-3 h-3" />
                       )}
                    </div>

                    <div className="flex-1 pt-1">
                       <div className="flex justify-between items-center">
                         <h5 className={`text-sm font-medium ${isPending ? 'text-slate-500' : 'text-slate-200'}`}>
                           {step.text}
                         </h5>
                         {isCurrent && <span className="text-[10px] text-cyan-400 font-medium animate-pulse">PROCESSING</span>}
                       </div>
                       <p className={`text-xs mt-0.5 ${isCurrent ? 'text-cyan-400/80' : 'text-slate-600'}`}>
                         {step.detail}
                       </p>
                    </div>
                 </div>
               )
             })}
          </div>

          {/* Overall Progress Bar */}
          <div className="mt-6 h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ease-out ${
                isComplete
                  ? 'bg-gradient-to-r from-emerald-500 to-cyan-500'
                  : 'bg-gradient-to-r from-cyan-500 to-blue-500'
              }`}
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>

        </div>

      </div>

      {/* Custom animation styles */}
      <style>{`
        @keyframes scan {
          0%, 100% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes scanOverlay {
          0% { height: 0%; opacity: 0; }
          50% { opacity: 0.2; }
          100% { height: 100%; opacity: 0; }
        }
        .animate-scan {
          animation: scan 2s ease-in-out infinite;
        }
        .animate-scanOverlay {
          animation: scanOverlay 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
}

// Edit Account Modal Component - supports single or multiple items
const EditAccountModal = ({
  isOpen,
  item,
  items,
  accounts,
  isSaving,
  onSave,
  onSaveMultiple,
  onClose
}: {
  isOpen: boolean
  item: LineItem | null // Single item edit
  items: LineItem[] // Multiple items edit
  accounts: Array<{ id: number; account_code: string; account_name: string }>
  isSaving: boolean
  onSave: (itemId: number, accountCode: string, accountName: string, reason: string) => void
  onSaveMultiple: (itemIds: number[], accountCode: string, accountName: string, reason: string) => void
  onClose: () => void
}) => {
  const [selectedAccount, setSelectedAccount] = useState('')
  const [reason, setReason] = useState('')

  // Bulk edit if items array has any items (could be 1 or more)
  const isBulkEdit = items.length > 0
  const displayItems = isBulkEdit ? items : (item ? [item] : [])

  // Get effective account (effective_account reflects user_selection if modified)
  const getEffectiveAccount = (lineItem: LineItem) => {
    if (lineItem.effectiveAccount) {
      return `${lineItem.effectiveAccount.account_code}|${lineItem.effectiveAccount.account_name}`
    }
    if (lineItem.suggestedAccount) {
      return `${lineItem.suggestedAccount.account_code}|${lineItem.suggestedAccount.account_name}`
    }
    return ''
  }

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (isBulkEdit) {
        // For bulk edit, start with empty selection
        setSelectedAccount('')
      } else if (item) {
        setSelectedAccount(getEffectiveAccount(item))
      }
      setReason('')
    }
  }, [item, items, isOpen, isBulkEdit])

  if (!isOpen || displayItems.length === 0) return null

  const originalAccount = !isBulkEdit && item ? getEffectiveAccount(item) : ''
  const hasChanged = isBulkEdit ? selectedAccount !== '' : selectedAccount !== originalAccount

  const handleSave = () => {
    if (!selectedAccount || !reason.trim() || isSaving) return
    const [code, name] = selectedAccount.split('|')

    if (isBulkEdit) {
      onSaveMultiple(items.map(i => i.id), code, name, reason.trim())
    } else if (item) {
      onSave(item.id, code, name, reason.trim())
    }
  }

  const handleClose = () => {
    if (isSaving) return
    setSelectedAccount(originalAccount)
    setReason('')
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div>
            <h3 className="text-lg font-semibold text-white">
              {isBulkEdit && items.length > 1 ? 'Bulk Edit Account Assignment' : 'Edit Account Assignment'}
            </h3>
            {isBulkEdit && items.length > 1 && (
              <p className="text-sm text-slate-400 mt-0.5">
                Applying to {items.length} selected items
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-1 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Line Items Info */}
          {isBulkEdit && items.length > 1 ? (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Selected Line Items ({items.length})</p>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {items.map((lineItem, idx) => (
                  <div key={lineItem.id} className="flex justify-between items-center text-sm">
                    <span className="text-slate-300 truncate flex-1 mr-2">{lineItem.desc}</span>
                    <span className="text-cyan-400 font-mono whitespace-nowrap">MYR {lineItem.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-slate-700/50 flex justify-between">
                <span className="text-xs text-slate-500">Total</span>
                <span className="text-sm text-cyan-400 font-mono font-medium">
                  MYR {items.reduce((sum, i) => sum + i.amount, 0).toFixed(2)}
                </span>
              </div>
            </div>
          ) : displayItems.length === 1 && (
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Line Item</p>
              <p className="text-sm text-white font-medium">{displayItems[0].desc}</p>
              <p className="text-sm text-cyan-400 font-mono mt-1">
                {displayItems[0].quantity && `${displayItems[0].quantity} × `}MYR {displayItems[0].amount.toFixed(2)}
              </p>
            </div>
          )}

          {/* Current Account - only show for single item edit (including single item from bulk selection) */}
          {displayItems.length === 1 && (displayItems[0].effectiveAccount || displayItems[0].suggestedAccount) && (() => {
            const singleItem = displayItems[0]
            return (
              <div className={`rounded-lg p-3 border ${singleItem.isUserModified ? 'bg-cyan-500/5 border-cyan-500/20' : 'bg-slate-800/30 border-slate-700/50'}`}>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  Current Assignment
                  {singleItem.isUserModified && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 font-medium">
                      User Modified
                    </span>
                  )}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-mono px-2 py-1 bg-slate-800 border border-slate-700 rounded text-slate-200">
                    {singleItem.effectiveAccount
                      ? `${singleItem.effectiveAccount.account_code} - ${singleItem.effectiveAccount.account_name}`
                      : singleItem.suggestedAccount
                      ? `${singleItem.suggestedAccount.account_code} - ${singleItem.suggestedAccount.account_name}`
                      : 'No account'}
                  </span>
                  {!singleItem.isUserModified && singleItem.suggestedAccount && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      singleItem.suggestedAccount.confidence >= 0.9
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : singleItem.suggestedAccount.confidence >= 0.7
                        ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                        : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                    }`}>
                      {Math.round(singleItem.suggestedAccount.confidence * 100)}% AI confidence
                    </span>
                  )}
                </div>
                {singleItem.isUserModified && singleItem.userSelection?.change_reason && (
                  <p className="text-xs text-cyan-400/70 mt-2 flex items-start gap-1">
                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {singleItem.userSelection.change_reason}
                  </p>
                )}
                {!singleItem.isUserModified && singleItem.suggestedAccount?.reasoning && (
                  <p className="text-xs text-slate-500 mt-2 flex items-start gap-1">
                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {singleItem.suggestedAccount.reasoning}
                  </p>
                )}
              </div>
            )
          })()}

          {/* New Account Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              New Account
            </label>
            <div className="relative">
              <select
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className={`w-full pl-4 pr-10 py-3 text-sm border rounded-lg outline-none appearance-none focus:ring-2 focus:ring-cyan-500 transition-colors
                  ${hasChanged
                    ? 'border-amber-500/30 bg-amber-500/10 text-amber-100'
                    : 'border-slate-600 bg-slate-800 text-slate-200'}`}
              >
                <option value="">Select an account...</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={`${acc.account_code}|${acc.account_name}`}>
                    {acc.account_code} - {acc.account_name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-500 pointer-events-none" />
            </div>
          </div>

          {/* Reason for change */}
          {hasChanged && (
            <div className="animate-in fade-in slide-in-from-top-2">
              <label className="block text-sm font-medium text-amber-400 mb-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Reason for Change <span className="text-amber-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Please explain why you're changing this account assignment..."
                className="w-full p-3 text-sm border border-amber-500/30 rounded-lg focus:ring-2 focus:ring-amber-500/50 outline-none min-h-[100px] bg-amber-500/5 text-amber-100 placeholder-amber-500/40"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-700 bg-slate-800/30">
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanged || !reason.trim() || isSaving}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2
              ${hasChanged && reason.trim() && !isSaving
                ? 'text-white bg-cyan-600 hover:bg-cyan-500 shadow-lg shadow-cyan-500/20'
                : 'text-slate-500 bg-slate-700 cursor-not-allowed'}`}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// Line Item Row Component (non-editable, opens modal)
const LineItemRow = ({
  item,
  currency,
  showQty,
  isSelected,
  onSelect,
  onEditClick
}: {
  item: LineItem
  currency: string
  showQty: boolean
  isSelected: boolean
  onSelect: (item: LineItem, selected: boolean) => void
  onEditClick: (item: LineItem) => void
}) => {
  // Get effective account to display
  const displayAccount = item.effectiveAccount || item.suggestedAccount

  return (
    <tr className={`hover:bg-slate-800/30 ${item.isUserModified ? 'bg-cyan-500/5' : ''} ${isSelected ? 'bg-purple-500/10' : ''}`}>
      <td className="px-4 py-2.5 w-10">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(item, e.target.checked)}
          className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer"
        />
      </td>
      <td className="px-4 py-2.5 text-slate-300">{item.desc}</td>
      {showQty && (
        <td className="px-4 py-2.5 text-center text-slate-400">
          {item.quantity || '-'}
        </td>
      )}
      <td className="px-4 py-2.5 text-right font-mono text-slate-400">
        {currency} {item.amount.toFixed(2)}
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            {displayAccount ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                    item.isUserModified
                      ? 'bg-cyan-500/10 border border-cyan-500/30 text-cyan-200'
                      : 'bg-slate-800 border border-slate-700 text-slate-200'
                  }`}>
                    {displayAccount.account_code} - {displayAccount.account_name}
                  </span>
                  {item.isUserModified ? (
                    <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                      Modified
                    </span>
                  ) : item.suggestedAccount && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                      item.suggestedAccount.confidence >= 0.9
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : item.suggestedAccount.confidence >= 0.7
                        ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                        : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                    }`}>
                      {Math.round(item.suggestedAccount.confidence * 100)}%
                    </span>
                  )}
                </div>
                {item.isUserModified && item.userSelection?.change_reason ? (
                  <div className="flex items-start gap-1 text-xs text-cyan-400/70 group cursor-help" title={item.userSelection.change_reason}>
                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-1">
                      {item.userSelection.change_reason}
                    </span>
                  </div>
                ) : item.suggestedAccount?.reasoning && (
                  <div className="flex items-start gap-1 text-xs text-slate-500 group cursor-help" title={item.suggestedAccount.reasoning}>
                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-1">
                      {item.suggestedAccount.reasoning}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <span className="text-xs text-slate-500 italic">
                No suggestion
              </span>
            )}
          </div>
          <button
            onClick={() => onEditClick(item)}
            className="p-1 text-slate-500 hover:text-cyan-400 transition-colors"
            title="Edit account"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  )
}

// Document Card Component
const DocumentCard = ({
  doc,
  selectedItemIds,
  onSave,
  onEditLineItem,
  onSelectItem,
  onSelectAll,
  onBulkEdit
}: {
  doc: ProcessedDocument
  selectedItemIds: Set<number>
  onSave: (id: string, updates: Partial<ProcessedDocument>) => void
  onEditLineItem: (docId: string, item: LineItem) => void
  onSelectItem: (docId: string, item: LineItem, selected: boolean) => void
  onSelectAll: (docId: string, items: LineItem[], selected: boolean) => void
  onBulkEdit: (docId: string, items: LineItem[]) => void
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const handleVerify = () => {
    onSave(doc.id, {
      status: 'verified',
    })
  }

  const handleEditClick = (item: LineItem) => {
    onEditLineItem(doc.id, item)
  }

  const handleSelectItem = (item: LineItem, selected: boolean) => {
    onSelectItem(doc.id, item, selected)
  }

  const handleSelectAll = (selected: boolean) => {
    onSelectAll(doc.id, doc.lineItems, selected)
  }

  const handleBulkEdit = () => {
    const selectedItems = doc.lineItems.filter(item => selectedItemIds.has(item.id))
    if (selectedItems.length > 0) {
      onBulkEdit(doc.id, selectedItems)
    }
  }

  const allSelected = doc.lineItems.length > 0 && doc.lineItems.every(item => selectedItemIds.has(item.id))
  const someSelected = doc.lineItems.some(item => selectedItemIds.has(item.id))
  const selectedCount = doc.lineItems.filter(item => selectedItemIds.has(item.id)).length

  return (
    <div className={`rounded-xl border shadow-sm transition-all duration-200 overflow-hidden bg-slate-900/60 backdrop-blur-xl
      ${doc.status === 'verified' ? 'border-slate-700/50' : 'border-cyan-500/30 ring-1 ring-cyan-500/20'}`}>

      {/* Header Section */}
      <div className="p-5 border-b border-slate-700/50 bg-slate-800/40 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg shadow-sm">
            <FileText className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white flex items-center gap-2">
              {doc.fileName}
              <span className="text-xs font-normal text-slate-400 px-2 py-0.5 bg-slate-800/50 rounded-full border border-slate-700/50">
                {doc.fileSize}
              </span>
            </h3>
            <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
              <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {doc.vendor}</span>
              <span className="hidden sm:inline text-slate-600">|</span>
              <span className="hidden sm:flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {doc.date}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between md:justify-end gap-4 w-full md:w-auto">
          <div className="text-right">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Total Amount</p>
            <p className="text-xl font-bold text-white">{doc.currency} {doc.amount.toFixed(2)}</p>
          </div>
          <ConfidenceBadge score={doc.confidence} />
          {doc.status === 'verified' && (
             <span className="flex items-center gap-1 text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full text-xs font-medium border border-emerald-500/20">
               <CheckCircle2 className="w-3.5 h-3.5" /> Verified
             </span>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-5">
        {/* Details Row */}
        <div className="flex flex-wrap gap-6 text-sm mb-4">
          <div>
            <span className="text-slate-500">Document #</span>
            <span className="ml-2 font-medium text-slate-200">{doc.docNum}</span>
          </div>
          <div>
            <span className="text-slate-500">Date</span>
            <span className="ml-2 font-medium text-slate-200">{doc.date}</span>
          </div>
          <div>
            <span className="text-slate-500">Type</span>
            <span className="ml-2 font-medium text-slate-200">{doc.transactionType}</span>
          </div>
        </div>

        {/* Accordion: Line Items */}
        {doc.lineItems.length > 0 && (
          <div className="mt-6 border-t border-slate-700/50 pt-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-cyan-400 font-medium py-2 transition-colors w-full group"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {isExpanded ? 'Hide Line Items' : `View ${doc.lineItems.length} Line Items`}
              <span className="flex-1 border-b border-dashed border-slate-700 ml-2 group-hover:border-cyan-500/30"></span>
            </button>

            {isExpanded && (
              <div className="mt-3 bg-slate-900/50 rounded-lg border border-slate-700/50 overflow-hidden animate-in slide-in-from-top-2">
                {/* Bulk Action Bar */}
                {someSelected && (
                  <div className="px-4 py-2 bg-purple-500/10 border-b border-purple-500/20 flex items-center justify-between">
                    <span className="text-sm text-purple-300">
                      {selectedCount} item{selectedCount > 1 ? 's' : ''} selected
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSelectAll(false)}
                        className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded hover:bg-slate-700 transition-colors"
                      >
                        Clear
                      </button>
                      <button
                        onClick={handleBulkEdit}
                        className="text-xs text-white bg-purple-600 hover:bg-purple-500 px-3 py-1 rounded-lg flex items-center gap-1 transition-colors"
                      >
                        <Edit2 className="w-3 h-3" />
                        Edit Selected
                      </button>
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left min-w-[650px]">
                    <thead className="bg-slate-800/50 text-slate-400 font-medium border-b border-slate-700/50">
                      <tr>
                        <th className="px-4 py-2 w-10">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            ref={(el) => {
                              if (el) el.indeterminate = someSelected && !allSelected
                            }}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer"
                          />
                        </th>
                        <th className="px-4 py-2">Description</th>
                        {doc.lineItems.some(item => item.quantity !== undefined) && (
                          <th className="px-4 py-2 text-center">Qty</th>
                        )}
                        <th className="px-4 py-2 text-right">Amount</th>
                        <th className="px-4 py-2">Suggested Account</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                      {doc.lineItems.map((item) => (
                        <LineItemRow
                          key={item.id}
                          item={item}
                          currency={doc.currency}
                          showQty={doc.lineItems.some(li => li.quantity !== undefined)}
                          isSelected={selectedItemIds.has(item.id)}
                          onSelect={handleSelectItem}
                          onEditClick={handleEditClick}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Verify Button for pending documents */}
        {doc.status === 'pending_review' && (
          <div className="mt-6 pt-4 border-t border-slate-700/50">
            <button
              onClick={handleVerify}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-lg hover:bg-cyan-500 transition-colors shadow-lg shadow-cyan-500/20"
            >
              <CheckCircle2 className="w-4 h-4" />
              Verify Document
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// Table Row Component
const TableRow = ({
  doc,
  selectedItemIds,
  onSave,
  onEditLineItem,
  onSelectItem,
  onSelectAll,
  onBulkEdit
}: {
  doc: ProcessedDocument
  selectedItemIds: Set<number>
  onSave: (id: string, updates: Partial<ProcessedDocument>) => void
  onEditLineItem: (docId: string, item: LineItem) => void
  onSelectItem: (docId: string, item: LineItem, selected: boolean) => void
  onSelectAll: (docId: string, items: LineItem[], selected: boolean) => void
  onBulkEdit: (docId: string, items: LineItem[]) => void
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const handleVerify = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSave(doc.id, {
      status: 'verified',
    })
    setIsExpanded(false)
  }

  const handleEditClick = (item: LineItem) => {
    onEditLineItem(doc.id, item)
  }

  const handleSelectItem = (item: LineItem, selected: boolean) => {
    onSelectItem(doc.id, item, selected)
  }

  const handleSelectAll = (selected: boolean) => {
    onSelectAll(doc.id, doc.lineItems, selected)
  }

  const handleBulkEdit = () => {
    const selectedItems = doc.lineItems.filter(item => selectedItemIds.has(item.id))
    if (selectedItems.length > 0) {
      onBulkEdit(doc.id, selectedItems)
    }
  }

  const allSelected = doc.lineItems.length > 0 && doc.lineItems.every(item => selectedItemIds.has(item.id))
  const someSelected = doc.lineItems.some(item => selectedItemIds.has(item.id))
  const selectedCount = doc.lineItems.filter(item => selectedItemIds.has(item.id)).length

  return (
    <>
      <tr
        onClick={() => setIsExpanded(!isExpanded)}
        className={`group cursor-pointer transition-colors border-b border-slate-700/50 ${isExpanded ? 'bg-cyan-500/5 border-cyan-500/20' : 'hover:bg-slate-700/20'}`}
      >
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${doc.status === 'verified' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-700 text-slate-400'}`}>
              {doc.status === 'verified' ? <CheckCircle2 className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
            </div>
            <div>
              <div className="font-medium text-white">{doc.fileName}</div>
              <div className="text-xs text-slate-500">{doc.docNum}</div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4">
          <div className="text-sm font-medium text-slate-300">{doc.vendor}</div>
          <div className="text-xs text-slate-500">{doc.date}</div>
        </td>
        <td className="px-6 py-4 text-sm">
          <div className="flex items-center gap-2">
            <ConfidenceBadge score={doc.confidence} />
          </div>
        </td>
        <td className="px-6 py-4 text-right font-mono text-sm text-white">
          {doc.currency} {doc.amount.toFixed(2)}
        </td>
        <td className="px-6 py-4 text-right">
          {isExpanded ? <ChevronUp className="w-4 h-4 ml-auto text-slate-500" /> : <ChevronDown className="w-4 h-4 ml-auto text-slate-500" />}
        </td>
      </tr>

      {/* Expanded Details Row */}
      {isExpanded && (
        <tr className="bg-slate-900/30 border-b border-slate-700/50">
          <td colSpan={5} className="px-6 py-4">
            <div className="space-y-4">
              {/* Line Items with per-item COA */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase">Line Items with AI-Suggested Accounts</h4>
                  {someSelected && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleBulkEdit(); }}
                      className="text-xs text-white bg-purple-600 hover:bg-purple-500 px-3 py-1 rounded-lg flex items-center gap-1 transition-colors"
                    >
                      <Edit2 className="w-3 h-3" />
                      Edit {selectedCount} Selected
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[550px]">
                    <thead className="text-xs text-slate-500 uppercase">
                      <tr className="border-b border-slate-700/50">
                        <th className="px-3 py-2 w-10">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            ref={(el) => {
                              if (el) el.indeterminate = someSelected && !allSelected
                            }}
                            onChange={(e) => { e.stopPropagation(); handleSelectAll(e.target.checked); }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer"
                          />
                        </th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                        <th className="px-3 py-2 text-left">Suggested Account</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50 bg-slate-800/20">
                      {doc.lineItems.map((item) => (
                        <LineItemRow
                          key={item.id}
                          item={item}
                          currency={doc.currency}
                          showQty={false}
                          isSelected={selectedItemIds.has(item.id)}
                          onSelect={handleSelectItem}
                          onEditClick={handleEditClick}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Actions */}
              {doc.status === 'pending_review' && (
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-700/50">
                  <button
                    onClick={(e) => { e.stopPropagation(); setIsExpanded(false); }}
                    className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleVerify}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-cyan-600 hover:bg-cyan-500 rounded-lg shadow-lg shadow-cyan-500/20 flex items-center gap-1 transition-colors"
                  >
                    <CheckCircle2 className="w-3 h-3" /> Verify
                  </button>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

export default function ChatSessionPage() {
  const navigate = useNavigate()
  const params = useParams()
  const sessionId = useMemo(() => params.sessionId as string, [params.sessionId])
  const sessionIdNumber = useMemo(() => parseInt(sessionId, 10), [sessionId])

  const [user, setUser] = useState<User | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [documents, setDocuments] = useState<ProcessedDocument[]>([])
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [isLoading, setIsLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [chartOfAccounts, setChartOfAccounts] = useState<Array<{ id: number; account_code: string; account_name: string }>>([])

  // Modal state for editing line item accounts
  const [editingDocId, setEditingDocId] = useState<string | null>(null)
  const [editingItem, setEditingItem] = useState<LineItem | null>(null)
  const [editingItems, setEditingItems] = useState<LineItem[]>([]) // For bulk edit

  // Selection state for bulk operations
  const [selectedItems, setSelectedItems] = useState<Map<string, Set<number>>>(new Map()) // docId -> Set of item ids
  const [isModalOpen, setIsModalOpen] = useState(false)

  // File viewer modal state
  const [viewingFile, setViewingFile] = useState<Attachment | null>(null)

  useEffect(() => {
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      setUser(JSON.parse(storedUser))
    }
  }, [])

  // Fetch chart of accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await apiService.getChartOfAccounts()
        if (response.success && response.data) {
          const accounts = (response.data as any[]).map((acc: any) => ({
            id: acc.id,
            account_code: acc.account_code,
            account_name: acc.account_name
          }))
          setChartOfAccounts(accounts)
        }
      } catch (error) {
        console.error('Failed to fetch chart of accounts:', error)
      }
    }
    fetchAccounts()
  }, [])

  // Fetch chat session data
  const { data: sessionData, isLoading: isLoadingSession, error, refetch: refetchSession } = useQuery({
    queryKey: ['chatSession', sessionId],
    queryFn: async () => {
      const response = await apiService.request<ChatSession>(`/chat_sessions/${sessionId}`)
      if (response.success && response.data) {
        return response.data
      }
      throw new Error(response.message || 'Failed to load chat session')
    },
    enabled: !!sessionId
  })

  // Parse markdown-style message content for stored messages
  const parseMarkdownMessage = (content: string): ProcessedDocument | null => {
    if (!content.includes('**File Processing Complete**')) {
      return null
    }

    const extractField = (text: string, fieldName: string): string => {
      const pattern = new RegExp(`\\*\\*${fieldName}:\\*\\*\\s*([^\\n*]+)`, 'i')
      const match = text.match(pattern)
      return match ? match[1].trim() : ''
    }

    const extractConfidence = (text: string): number => {
      const match = text.match(/🟢\s*(\d+(?:\.\d+)?)%/)
      return match ? parseInt(match[1]) / 100 : 0
    }

    const extractLineItems = (text: string): LineItem[] => {
      const lineItemsSection = text.split('**Line Items:**')[1]
      if (!lineItemsSection) return []

      return lineItemsSection.split('\n')
        .filter(line => line.trim().startsWith('- '))
        .map((line, index) => {
          const cleaned = line.replace('- ', '').trim()
          // Try to extract amount from line
          const amountMatch = cleaned.match(/([A-Z]{3})\s*([\d,]+\.?\d*)/)
          return {
            id: index + 1,
            index: index,
            desc: cleaned.split(':')[0] || cleaned,
            amount: amountMatch ? parseFloat(amountMatch[2].replace(',', '')) : 0
          }
        })
        .filter(line => line.desc.length > 0)
    }

    const extractAccount = (text: string): string => {
      const accountSection = text.split('**Suggested Account:**')[1]?.split('**')[0]
      return accountSection ? accountSection.trim() : 'Not specified'
    }

    const amountStr = extractField(content, 'Amount')
    const amountMatch = amountStr.match(/([\d,]+\.?\d*)/)
    const amount = amountMatch ? parseFloat(amountMatch[1].replace(',', '')) : 0

    return {
      id: `doc-${Date.now()}`,
      fileName: 'Uploaded Document',
      fileSize: 'N/A',
      vendor: extractField(content, 'Vendor/Customer') || 'Unknown',
      transactionType: extractField(content, 'Transaction Type') || 'Transaction',
      docType: extractField(content, 'Document Type') || 'Document',
      docNum: extractField(content, 'Document #') || 'Unknown',
      date: extractField(content, 'Date') || 'Unknown',
      amount: amount,
      currency: 'MYR',
      confidence: extractConfidence(content),
      suggestedAccount: extractAccount(content),
      status: 'pending_review',
      userFeedback: '',
      lineItems: extractLineItems(content)
    }
  }

  // Format structured processing results
  const formatProcessingResult = (data: any): ProcessedDocument | null => {
    if (data.classification && data.extracted_data) {
      const { classification, extracted_data } = data

      // Parse line items with per-item COA suggestions from structured data
      let lineItems: LineItem[] = []

      // First try to get line items from extracted_data (new API format)
      if (extracted_data.line_items && Array.isArray(extracted_data.line_items)) {
        lineItems = extracted_data.line_items.map((item: any, index: number) => ({
          id: index + 1,
          desc: item.description || item.desc || '',
          quantity: item.quantity,
          unit_price: item.unit_price,
          amount: item.amount || 0,
          suggestedAccount: item.suggested_account ? {
            account_code: item.suggested_account.account_code,
            account_name: item.suggested_account.account_name,
            confidence: item.suggested_account.confidence || 0,
            reasoning: item.suggested_account.reasoning || ''
          } : undefined
        }))
      } else if (data.message && data.message.includes('**Line Items:**')) {
        // Fallback to parsing from message text
        const lineItemsSection = data.message.split('**Line Items:**')[1]
        if (lineItemsSection) {
          lineItems = lineItemsSection.split('\n')
            .filter((line: string) => line.trim().startsWith('- '))
            .map((line: string, index: number) => {
              const cleaned = line.replace('- ', '').trim()
              const amountMatch = cleaned.match(/([A-Z]{3})\s*([\d,]+\.?\d*)/)
              return {
                id: index + 1,
                desc: cleaned.split(':')[0] || cleaned,
                amount: amountMatch ? parseFloat(amountMatch[2].replace(',', '')) : 0
              }
            })
        }
      }

      // Get default/file-level suggested account
      let suggestedAccount = 'Not specified'
      if (extracted_data.suggested_account) {
        if (typeof extracted_data.suggested_account === 'string') {
          suggestedAccount = extracted_data.suggested_account
        } else if (extracted_data.suggested_account.account_code) {
          suggestedAccount = `${extracted_data.suggested_account.account_code} - ${extracted_data.suggested_account.account_name}`
        }
      } else if (data.message && data.message.includes('**Suggested Account:**')) {
        const accountSection = data.message.split('**Suggested Account:**')[1]?.split('**')[0]
        if (accountSection) {
          suggestedAccount = accountSection.trim()
        }
      }

      return {
        id: `doc-${data.message_id || Date.now()}`,
        fileName: data.filename || 'Uploaded Document',
        fileSize: data.file_size || 'N/A',
        vendor: extracted_data.vendor_or_customer || 'Unknown',
        transactionType: classification.transaction_type || 'Transaction',
        docType: classification.document_type || 'Document',
        docNum: extracted_data.document_number || 'Unknown',
        date: extracted_data.date || 'Unknown',
        amount: extracted_data.total_amount || 0,
        currency: 'MYR',
        confidence: classification.confidence || 0,
        suggestedAccount: suggestedAccount,
        status: data.requires_review ? 'pending_review' : 'verified',
        userFeedback: '',
        lineItems: lineItems,
        fileUploadId: data.file_upload_id
      }
    }
    return null
  }

  // WebSocket connection for real-time updates
  const handleFileProcessingComplete = useCallback((data: any) => {
    console.log('Processing complete data:', data)

    const processedDoc = formatProcessingResult(data)

    if (processedDoc) {
      setDocuments(prev => [...prev, processedDoc])
    }

    // Add assistant message to chat
    const assistantMessage: Message = {
      id: data.message_id || Date.now(),
      content: data.message || 'File processing completed',
      role: 'assistant',
      attachments_count: 0,
      created_at: data.timestamp || new Date().toISOString()
    }

    setMessages(prev => [...prev, assistantMessage])

    // Refresh session data to get updated file_uploads
    refetchSession()
  }, [refetchSession])

  const handleWebSocketError = useCallback((error: Error) => {
    console.error('WebSocket error:', error)
  }, [])

  const {
    connectionStatus,
    connect: connectWebSocket,
    disconnect: disconnectWebSocket,
  } = useWebSocket({
    chatSessionId: sessionIdNumber && !isNaN(sessionIdNumber) ? sessionIdNumber : undefined,
    onFileProcessingComplete: handleFileProcessingComplete,
    onError: handleWebSocketError,
    autoConnect: true
  })

  useEffect(() => {
    if (sessionData) {
      // Initialize messages from session data if available
      if (sessionData.messages && sessionData.messages.length > 0) {
        setMessages(sessionData.messages)
      }

      // Create documents from file_uploads with extracted_data (new API format)
      if (sessionData.file_uploads && sessionData.file_uploads.length > 0) {
        const docs: ProcessedDocument[] = []

        // Get all attachments from user message for matching
        const firstUserMessage = sessionData.messages?.find(m => m.role === 'user' && m.attachments_count > 0)
        const attachments = firstUserMessage?.attachments || []

        sessionData.file_uploads.forEach((upload, index) => {
          if (upload.extracted_data) {
            const { extracted_data } = upload

            // Try to find the matching attachment for this file_upload
            // Priority: 1) attachment_id match, 2) index-based match, 3) filename from upload
            let matchedAttachment: Attachment | undefined

            if (upload.attachment_id) {
              matchedAttachment = attachments.find(a => a.id === upload.attachment_id)
            }

            if (!matchedAttachment && attachments.length > index) {
              matchedAttachment = attachments[index]
            }

            // Get filename: prefer upload fields, then matched attachment, then fallback
            const fileName = upload.original_filename || upload.filename || matchedAttachment?.filename || `Document ${index + 1}`
            const fileSize = upload.file_size
              ? `${(upload.file_size / 1024).toFixed(1)} KB`
              : matchedAttachment
                ? `${(matchedAttachment.byte_size / 1024).toFixed(1)} KB`
                : 'N/A'

            // Map line items with per-item COA suggestions
            const lineItems: LineItem[] = extracted_data.line_items.map((item, idx) => ({
              id: idx + 1,
              index: idx, // line_item_index for API calls
              desc: item.description,
              quantity: item.quantity,
              unit_price: item.unit_price,
              amount: item.amount,
              isTax: item.is_tax,
              suggestedAccount: item.suggested_account ? {
                account_code: item.suggested_account.account_code,
                account_name: item.suggested_account.account_name,
                confidence: item.suggested_account.confidence,
                reasoning: item.suggested_account.reasoning
              } : undefined,
              effectiveAccount: item.effective_account,
              isUserModified: item.is_user_modified,
              userSelection: item.user_selection
            }))

            docs.push({
              id: `doc-${upload.id}`,
              fileName: fileName,
              fileSize: fileSize,
              vendor: extracted_data.vendor_or_customer,
              transactionType: 'Expense',
              docType: 'Invoice',
              docNum: extracted_data.document_number,
              date: extracted_data.document_date,
              amount: extracted_data.total_amount,
              currency: extracted_data.currency || 'MYR',
              confidence: upload.confidence,
              suggestedAccount: upload.suggested_account
                ? `${upload.suggested_account.account_code} - ${upload.suggested_account.account_name}`
                : 'Not specified',
              status: upload.requires_review ? 'pending_review' : 'pending_review',
              userFeedback: '',
              lineItems: lineItems,
              fileUploadId: upload.id
            })
          }
        })

        if (docs.length > 0) {
          setDocuments(docs)
        }
      }
    }
  }, [sessionData])

  const handleSaveDocument = (id: string, updates: Partial<ProcessedDocument>) => {
    setDocuments(prev => prev.map(doc =>
      doc.id === id ? { ...doc, ...updates } : doc
    ))
  }

  const handleLineItemUpdate = (docId: string, itemId: number, updatedLineItem: {
    effective_account: EffectiveAccount
    is_user_modified: boolean
    user_selection: UserSelection | null
  }) => {
    setDocuments(prev => prev.map(doc => {
      if (doc.id !== docId) return doc

      const updatedLineItems = doc.lineItems.map(item => {
        if (item.id !== itemId) return item

        return {
          ...item,
          effectiveAccount: updatedLineItem.effective_account,
          isUserModified: updatedLineItem.is_user_modified,
          userSelection: updatedLineItem.user_selection
        }
      })

      return { ...doc, lineItems: updatedLineItems }
    }))
  }

  // Modal handlers
  const handleEditLineItem = (docId: string, item: LineItem) => {
    setEditingDocId(docId)
    setEditingItem(item)
    setIsModalOpen(true)
  }

  const [isSavingAccount, setIsSavingAccount] = useState(false)

  const handleModalSave = async (itemId: number, accountCode: string, accountName: string, reason: string) => {
    if (!editingDocId || !editingItem) return

    // Find the document to get the fileUploadId
    const doc = documents.find(d => d.id === editingDocId)
    if (!doc?.fileUploadId) {
      console.error('No fileUploadId found for document')
      return
    }

    setIsSavingAccount(true)

    try {
      const response = await apiService.updateLineItemAccount(doc.fileUploadId.toString(), {
        line_item_index: editingItem.index,
        account_code: accountCode,
        change_reason: reason
      })

      if (response.success && response.data) {
        const data = response.data as any
        // Update local state with the response - use the line_item from API response
        const lineItem = data.line_item || data
        handleLineItemUpdate(editingDocId, itemId, {
          effective_account: lineItem.effective_account || { account_code: accountCode, account_name: accountName },
          is_user_modified: lineItem.is_user_modified ?? true,
          user_selection: lineItem.user_selection || { account_code: accountCode, account_name: accountName, change_reason: reason }
        })
        handleModalClose()
      } else {
        console.error('Failed to update line item account:', response.message || response.error)
        // Could show error toast here
      }
    } catch (error) {
      console.error('Error updating line item account:', error)
      // Could show error toast here
    } finally {
      setIsSavingAccount(false)
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingItem(null)
    setEditingItems([])
    setEditingDocId(null)
  }

  // Selection handlers
  const handleSelectItem = (docId: string, item: LineItem, selected: boolean) => {
    setSelectedItems(prev => {
      const newMap = new Map(prev)
      const docItems = newMap.get(docId) || new Set<number>()
      const newDocItems = new Set(docItems)

      if (selected) {
        newDocItems.add(item.id)
      } else {
        newDocItems.delete(item.id)
      }

      newMap.set(docId, newDocItems)
      return newMap
    })
  }

  const handleSelectAll = (docId: string, items: LineItem[], selected: boolean) => {
    setSelectedItems(prev => {
      const newMap = new Map(prev)
      if (selected) {
        newMap.set(docId, new Set(items.map(item => item.id)))
      } else {
        newMap.set(docId, new Set())
      }
      return newMap
    })
  }

  const handleBulkEdit = (docId: string, items: LineItem[]) => {
    setEditingDocId(docId)
    setEditingItems(items)
    setEditingItem(null)
    setIsModalOpen(true)
  }

  // Bulk save handler
  const handleModalSaveMultiple = async (itemIds: number[], accountCode: string, accountName: string, reason: string) => {
    if (!editingDocId || editingItems.length === 0) return

    const doc = documents.find(d => d.id === editingDocId)
    if (!doc?.fileUploadId) {
      console.error('No fileUploadId found for document')
      return
    }

    setIsSavingAccount(true)

    try {
      // Use batch API for bulk updates
      const response = await apiService.batchUpdateLineItemAccounts(doc.fileUploadId!.toString(), {
        line_items: editingItems.map(item => ({
          line_item_index: item.index,
          account_code: accountCode,
          change_reason: reason
        }))
      })

      if (response.success && response.data) {
        const data = response.data as any
        const updatedLineItems = data.line_items || []

        // Update local state for all items
        editingItems.forEach((editItem, idx) => {
          const updatedItem = updatedLineItems[idx] || {}
          handleLineItemUpdate(editingDocId, editItem.id, {
            effective_account: updatedItem.effective_account || { account_code: accountCode, account_name: accountName },
            is_user_modified: updatedItem.is_user_modified ?? true,
            user_selection: updatedItem.user_selection || { account_code: accountCode, account_name: accountName, change_reason: reason }
          })
        })

        // Clear selection for this document
        setSelectedItems(prev => {
          const newMap = new Map(prev)
          newMap.set(editingDocId, new Set())
          return newMap
        })

        handleModalClose()
      } else {
        console.error('Failed to batch update line item accounts:', response.message || response.error)
      }
    } catch (error) {
      console.error('Error updating line item accounts:', error)
    } finally {
      setIsSavingAccount(false)
    }
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inputValue.trim()) return

    // Add user message
    const userMessage: Message = {
      id: Date.now(),
      content: inputValue,
      role: 'user',
      attachments_count: 0,
      created_at: new Date().toISOString()
    }
    setMessages(prev => [...prev, userMessage])
    setInputValue('')

    setIsLoading(true)

    try {
      // Call the messages API to add a new message
      const response = await apiService.request<Message>(`/chat_sessions/${sessionId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content: inputValue, role: 'user' })
      })

      if (response.success && response.data) {
        // Update with actual message from server
        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== userMessage.id)
          return [...filtered, response.data!]
        })

        // Simulate assistant response
        setTimeout(() => {
          const aiMessage: Message = {
            id: Date.now() + 1,
            content: `I understand your request: "${inputValue}". I'm processing this and will provide you with detailed accounting insights shortly.`,
            role: 'assistant',
            attachments_count: 0,
            created_at: new Date().toISOString()
          }
          setMessages(prev => [...prev, aiMessage])
          setIsLoading(false)
        }, 1000)
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      setMessages(prev => prev.filter(m => m.id !== userMessage.id))
      setIsLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('user')
    navigate('/')
  }

  if (isLoadingSession) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-6">
        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-6 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">Error Loading Chat Session</h2>
          <p className="text-slate-400 mb-4">Failed to load the chat session. Please try again.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  const pendingCount = documents.filter(d => d.status === 'pending_review').length
  const verifiedCount = documents.filter(d => d.status === 'verified').length

  // Calculate if there are still files being processed
  const totalAttachments = sessionData?.messages?.[0]?.attachments?.length || 0
  const processedDocuments = documents.length
  const hasMoreFilesToProcess = totalAttachments > 0 && processedDocuments < totalAttachments
  const isStillProcessing = hasMoreFilesToProcess ||
    (sessionData?.file_uploads?.some(u => u.status === 'processing' || (u.status === 'completed' && !u.extracted_data)) ?? false)

  // Connection status badge for header
  const connectionBadge = (
    <div className="flex items-center gap-2">
      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
        connectionStatus === 'connected'
          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
          : connectionStatus === 'connecting'
          ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
          : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
      }`}>
        {connectionStatus === 'connected' ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
        <span className="hidden sm:inline">
          {connectionStatus === 'connected' ? 'Live' : connectionStatus === 'connecting' ? 'Connecting' : 'Offline'}
        </span>
      </div>
    </div>
  )

  return (
    <DashboardLayout
      user={user}
      onLogout={handleLogout}
      title={sessionData?.title || 'Document Review'}
      subtitle="Review AI-processed documents and verify classifications"
      titleIcon={<FileText className="w-6 h-6" />}
      headerActions={connectionBadge}
    >
      <div className="max-w-6xl mx-auto space-y-6 pb-32">
        {/* Uploaded Files Section */}
        {sessionData?.messages && sessionData.messages.length > 0 && sessionData.messages[0].attachments && sessionData.messages[0].attachments.length > 0 && (() => {
          const attachments = sessionData.messages[0].attachments
          const fileUploads = sessionData.file_uploads || []

          // Create a map to check if an attachment index has been processed
          // A file is processed if its corresponding file_upload has extracted_data
          const getFileProcessedStatus = (attachmentIndex: number, attachmentId: number): boolean => {
            // First try to match by attachment_id
            const matchByAttachmentId = fileUploads.find(fu => fu.attachment_id === attachmentId)
            if (matchByAttachmentId) {
              return !!matchByAttachmentId.extracted_data
            }
            // Fall back to index-based matching
            if (fileUploads.length > attachmentIndex) {
              return !!fileUploads[attachmentIndex].extracted_data
            }
            return false
          }

          return (
            <div className={`rounded-xl p-4 backdrop-blur-md border transition-all ${
              isStillProcessing
                ? 'bg-slate-900/80 border-cyan-500/30'
                : 'bg-slate-900/60 border-slate-700/50'
            }`}>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <FileText className="w-4 h-4 text-cyan-400" />
                  Uploaded Files
                  <span className="text-xs font-normal text-slate-500">
                    ({attachments.length} file{attachments.length > 1 ? 's' : ''})
                  </span>
                  {isStillProcessing && processedDocuments > 0 && (
                    <span className="text-xs font-medium text-cyan-400">
                      • {processedDocuments}/{totalAttachments} processed
                    </span>
                  )}
                </h3>
                {isStillProcessing ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full">
                    <Loader2 className="w-3 h-3 text-cyan-400 animate-spin" />
                    <span className="text-xs text-cyan-400 font-medium">
                      Processing {totalAttachments - processedDocuments} remaining...
                    </span>
                  </div>
                ) : connectionStatus === 'connected' && (
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                    Listening for updates
                  </span>
                )}
              </div>

              {/* File Cards */}
              <div className="space-y-2">
                {attachments.map((file, index) => {
                  const fileExt = file.filename.split('.').pop()?.toUpperCase() || 'FILE'
                  const isFileProcessed = getFileProcessedStatus(index, file.id)
                  const isFileProcessing = !isFileProcessed && isStillProcessing

                  // Check if file is viewable (image or PDF)
                  const isViewable = file.content_type?.startsWith('image/') || file.content_type === 'application/pdf'

                  return (
                    <div
                      key={file.id}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${
                        isFileProcessing
                          ? 'bg-slate-800/30 border-cyan-500/20'
                          : isFileProcessed
                          ? 'bg-emerald-500/5 border-emerald-500/20 hover:border-emerald-500/40'
                          : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
                      } ${isViewable ? 'cursor-pointer' : ''}`}
                      onClick={() => isViewable && setViewingFile(file)}
                    >
                      {/* File Icon */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center border relative overflow-hidden ${
                        isFileProcessing
                          ? 'bg-cyan-500/10 border-cyan-500/30'
                          : isFileProcessed
                          ? 'bg-emerald-500/10 border-emerald-500/30'
                          : 'bg-slate-700/50 border-slate-600/50'
                      }`}>
                        {isFileProcessing && (
                          <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/20 to-transparent animate-pulse" />
                        )}
                        {isFileProcessed ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <span className={`text-xs font-bold uppercase ${isFileProcessing ? 'text-cyan-400' : 'text-slate-400'}`}>
                            {fileExt}
                          </span>
                        )}
                      </div>

                      {/* File Info */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${
                          isFileProcessing ? 'text-cyan-200' : isFileProcessed ? 'text-emerald-200' : 'text-slate-200'
                        }`}>
                          {file.filename}
                        </p>
                        <p className="text-xs text-slate-500">
                          {(file.byte_size / 1024).toFixed(1)} KB
                          {isViewable && <span className="ml-1">• Click to view</span>}
                        </p>
                      </div>

                      {/* Status & Actions */}
                      <div className="flex items-center gap-2">
                        {isFileProcessing ? (
                          <div className="flex items-center gap-1.5 px-2 py-1 bg-cyan-500/10 rounded-md">
                            <Sparkles className="w-3 h-3 text-cyan-400 animate-pulse" />
                            <span className="text-[10px] text-cyan-400 font-medium uppercase tracking-wider">
                              Analyzing
                            </span>
                          </div>
                        ) : (
                          <>
                            {isFileProcessed && (
                              <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-500/10 rounded-md">
                                <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                                <span className="text-[10px] text-emerald-400 font-medium uppercase tracking-wider">
                                  Done
                                </span>
                              </div>
                            )}
                            {/* View button */}
                            {isViewable && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setViewingFile(file)
                                }}
                                className="p-1.5 text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors"
                                title="View file"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            )}
                            {/* Download button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                file.url && apiService.downloadFile(file.url, file.filename)
                              }}
                              className="p-1.5 text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors"
                              title="Download file"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })()}

            {/* Results Section - Only show if documents exist */}
            {documents.length > 0 && (
              <>
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h1 className="text-3xl font-bold text-white">Document Review</h1>
                    <p className="text-slate-400">Verify AI suggestions and classify your expenses.</p>
                  </div>

                  {/* View Switcher */}
                  <div className="flex items-center gap-1 bg-slate-800/50 p-1 rounded-xl border border-slate-700/50 backdrop-blur-xl">
                       <button className="p-2 text-slate-400 hover:bg-slate-700/50 hover:text-white rounded-lg transition-colors">
                         <Filter className="w-4 h-4" />
                       </button>
                       <div className="w-px h-4 bg-slate-700 mx-1"></div>
                       <button
                         onClick={() => setViewMode('cards')}
                         className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                           ${viewMode === 'cards' ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/30' : 'text-slate-400 hover:bg-slate-700/50'}`}
                       >
                         <LayoutGrid className="w-4 h-4" /> Cards
                       </button>
                       <button
                         onClick={() => setViewMode('table')}
                         className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                           ${viewMode === 'table' ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/30' : 'text-slate-400 hover:bg-slate-700/50'}`}
                       >
                         <List className="w-4 h-4" /> Table
                       </button>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-4 flex items-center gap-4 backdrop-blur-md shadow-lg">
                      <div className="p-3 bg-indigo-500/10 text-indigo-400 rounded-lg">
                        <BrainCircuit className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Documents Processed</p>
                        <p className="text-2xl font-bold text-white">{documents.length}</p>
                      </div>
                    </div>
                    <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-4 flex items-center gap-4 backdrop-blur-md shadow-lg">
                      <div className="p-3 bg-amber-500/10 text-amber-400 rounded-lg">
                        <Clock className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Pending Review</p>
                        <p className="text-2xl font-bold text-white">{pendingCount}</p>
                      </div>
                    </div>
                    <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-4 flex items-center gap-4 backdrop-blur-md shadow-lg">
                      <div className="p-3 bg-emerald-500/10 text-emerald-400 rounded-lg">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Verified</p>
                        <p className="text-2xl font-bold text-white">{verifiedCount}</p>
                      </div>
                    </div>
                  </div>

                {/* Content Area */}
                {viewMode === 'cards' ? (
                  <div className="space-y-6">
                    {documents.map(doc => (
                      <DocumentCard
                        key={doc.id}
                        doc={doc}
                        selectedItemIds={selectedItems.get(doc.id) || new Set()}
                        onSave={handleSaveDocument}
                        onEditLineItem={handleEditLineItem}
                        onSelectItem={handleSelectItem}
                        onSelectAll={handleSelectAll}
                        onBulkEdit={handleBulkEdit}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="bg-slate-800/40 rounded-xl border border-slate-700/50 backdrop-blur-xl overflow-x-auto">
                    <table className="w-full text-left min-w-[800px]">
                      <thead className="bg-slate-900/50 border-b border-slate-700/50">
                        <tr>
                          <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Document</th>
                          <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Vendor / Date</th>
                          <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Confidence</th>
                          <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider text-right">Amount</th>
                          <th className="px-6 py-4 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map(doc => (
                          <TableRow
                            key={doc.id}
                            doc={doc}
                            selectedItemIds={selectedItems.get(doc.id) || new Set()}
                            onSave={handleSaveDocument}
                            onEditLineItem={handleEditLineItem}
                            onSelectItem={handleSelectItem}
                            onSelectAll={handleSelectAll}
                            onBulkEdit={handleBulkEdit}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* All Done State */}
                {documents.every(d => d.status === 'verified') && documents.length > 0 && (
                  <div className="mt-12 text-center p-8 bg-emerald-500/5 rounded-2xl border border-emerald-500/20 max-w-2xl mx-auto backdrop-blur-md">
                    <div className="inline-flex p-4 bg-slate-900 rounded-full shadow-lg shadow-emerald-500/20 mb-4">
                      <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">All Caught Up!</h3>
                    <p className="text-slate-400 mb-6">You've reviewed all pending documents.</p>
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-medium shadow-lg hover:bg-emerald-500 transition-colors"
                    >
                      Back to Dashboard
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Processing State - Show when file uploads are still processing */}
            {isStillProcessing && (
              <ProcessingLoader />
            )}

            {/* Empty State - No documents yet and not processing */}
            {documents.length === 0 && messages.length === 0 && (!sessionData?.file_uploads || sessionData.file_uploads.length === 0) && (
              <div className="text-center py-16">
                <div className="inline-flex p-4 bg-slate-800/50 rounded-full mb-4">
                  <Sparkles className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Waiting for Documents</h3>
                <p className="text-slate-400 max-w-md mx-auto">
                  Upload documents from the dashboard to start processing. AI will analyze and categorize your expenses automatically.
                </p>
              </div>
            )}

            {/* Chat Messages */}
            {messages.length > 0 && documents.length === 0 && (!sessionData?.file_uploads || !sessionData.file_uploads.some(u => u.status === 'processing')) && (
              <div className="space-y-4">
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`p-4 rounded-xl ${
                      msg.role === 'user'
                        ? 'bg-cyan-500/10 border border-cyan-500/20 ml-12'
                        : 'bg-slate-800/50 border border-slate-700/50 mr-12'
                    }`}
                  >
                    <p className="text-sm text-slate-300">{msg.content}</p>
                  </div>
                ))}
              </div>
            )}

      {/* Chat Input - Fixed at bottom, responsive positioning */}
      <div className="fixed bottom-0 left-0 lg:left-72 right-0 p-4 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19] to-transparent z-20">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSendMessage} className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 rounded-xl opacity-0 group-hover:opacity-100 blur transition duration-500"></div>
            <div className="relative bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-xl flex items-center">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask about your documents..."
                className="flex-1 bg-transparent px-4 py-3 text-white placeholder-slate-500 focus:outline-none text-sm sm:text-base"
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                className={`m-2 p-2 rounded-lg transition-all ${
                  inputValue.trim()
                    ? 'bg-cyan-600 text-white hover:bg-cyan-500 shadow-lg shadow-cyan-500/20'
                    : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                }`}
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>

      {/* Edit Account Modal - rendered at page level for proper z-index */}
      <EditAccountModal
        isOpen={isModalOpen}
        item={editingItem}
        items={editingItems}
        accounts={chartOfAccounts}
        isSaving={isSavingAccount}
        onSave={handleModalSave}
        onSaveMultiple={handleModalSaveMultiple}
        onClose={handleModalClose}
      />

      {/* File Viewer Modal */}
      <FileViewerModal
        isOpen={viewingFile !== null}
        file={viewingFile}
        onClose={() => setViewingFile(null)}
        onDownload={(url, filename) => apiService.downloadFile(url, filename)}
      />
    </DashboardLayout>
  )
}
